import { useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import { DeleteOutlined } from "@ant-design/icons";

import AddContributorsModal from "@/features/datablocks/details/components/AddContributorsModal";
import { BaseUserResponse } from "@/redux/users/types";
import UserAvatar from "@/domains/users/components/UserAvatar";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { useEvidenceContributors } from "@/domains/evidence/hooks/useEvidenceFields";
import { usePatchDataBlockMutation } from "@/redux/service";
import { ContributorsProps } from "@/features/datablocks/details/types";

const Contributors = ({ dataBlock, canEdit, showCtas }: ContributorsProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [patchDataBlock] = usePatchDataBlockMutation();
  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const contributors = useEvidenceContributors(dataBlock);

  const deleteContributor = (deletedContributorId: number) => {
    const data = {
      fieldValues: [
        {
          field: dataBlock.fieldsMapping.contributors,
          value: (contributors.data as BaseUserResponse[])
            .filter((i) => i.id !== deletedContributorId)
            .map((i) => i.id),
        },
      ],
    };
    patchDataBlock({ id: dataBlock.id, data })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("datablocks.labels.contributorsDeleted"),
        }),
      );
  };

  return (
    <div className="flex flex-col gap-200">
      {canEdit && showCtas && (
        <div className="flex justify-end">
          <BaseButton
            variant={BUTTON_VARIANTS.LINK}
            size={BUTTON_SIZES.SMALL}
            leadingIcon={<PlusOutlined />}
            onClick={() => setContributorModalOpen(true)}
          >
            {t("datablocks.labels.addContributors")}
          </BaseButton>
        </div>
      )}

      <div className="flex flex-col gap-200">
        {contributors.isLoading ? (
          <BaseSpinner size="small" />
        ) : (
          (contributors.data as BaseUserResponse[]).map((contributor) => (
            <div key={contributor.id} className="flex gap-200 justify-between">
              <div className="data-block-sidebar-contributors-items-details flex gap-200 flex-1 items-center full-width">
                <UserAvatar user={contributor} />
                <span className="data-block-sidebar-contributors-items-names flex-1">
                  {contributor.fullName}
                </span>
              </div>

              {canEdit && showCtas && (
                <DeleteOutlined
                  onClick={() => deleteContributor(contributor.id)}
                />
              )}
            </div>
          ))
        )}
      </div>

      <AddContributorsModal
        open={contributorModalOpen}
        setOpen={setContributorModalOpen}
        dataBlock={dataBlock}
        currentContributorIds={
          contributors.isLoading
            ? []
            : (contributors.data as BaseUserResponse[]).map((i) => i.id)
        }
      />
    </div>
  );
};

export default Contributors;
